// PlanSwitchCard Component
// Displays plan information for the plan switch modal (similar to PlanCard but without button)

import React from 'react';

export function PlanSwitchCard({ planData, billingCycle }) {
  if (!planData) return null;

  const {
    planDisplayName,
    tierDisplayName,
    billingCycleDisplay,
    description,
    tierDescription,
    credits,
    price,
    monthlyPrice,
    discount,
  } = planData;

  // Format description into features list with source tracking (matches PlanCard)
  const planFeatures = description
    ? description.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature.trim(),
        source: 'plan'
      }))
    : [];
  
  // Format tier description into features list with source tracking
  const tierFeatures = tierDescription
    ? tierDescription.split('\n').filter(line => line.trim().startsWith('-')).map(feature => ({
        text: feature.trim(),
        source: 'tier'
      }))
    : [];
  
  // Combine plan and tier features
  const features = [...planFeatures, ...tierFeatures];

  // Format price
  const formattedPrice = price ? `$${price.toFixed(2)}` : 'Free';
  const priceSuffix = billingCycle === 'one-time' ? ' (one-time)' : 
                      billingCycle === 'annual' ? '/year' : '/month';

  // Format credits
  const formattedCredits = credits ? credits.toLocaleString() : '0';

  return (
    <div
      style={{
        width: '320px',
        minHeight: '500px',
        flexShrink: 0,
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '12px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Section: Title, Price, Credits */}
      <div style={{ flexShrink: 0 }}>
        {/* Plan Header */}
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
              marginBottom: '8px',
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '600',
                color: '#fff',
              }}
            >
              {planDisplayName}
            </h3>
            {tierDisplayName && tierDisplayName !== 'Basic' && (
              <div
                style={{
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: 'rgba(139, 0, 0, 0.3)',
                  borderRadius: '4px',
                  fontSize: '12px',
                  color: '#ff6b6b',
                  fontWeight: '500',
                }}
              >
                {tierDisplayName}
              </div>
            )}
          </div>
          <p style={{
            margin: 0,
            fontSize: '12px',
            color: '#999',
          }}>
            {billingCycleDisplay}
          </p>
        </div>

        {/* Price and Credits Row */}
        <div style={{ marginBottom: '16px' }}>
          {/* Price Display */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '4px' }}>
              <span
                style={{
                  fontSize: '36px',
                  fontWeight: '700',
                  color: '#fff',
                  lineHeight: '1',
                }}
              >
                {formattedPrice}
              </span>
              {price > 0 && (
                <span
                  style={{
                    fontSize: '16px',
                    color: '#999',
                    fontWeight: '400',
                  }}
                >
                  {priceSuffix}
                </span>
              )}
            </div>
            {billingCycle === 'annual' && discount > 0 && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#4ade80',
                  fontWeight: '500',
                }}
              >
                Save ${(monthlyPrice * 12 - price).toFixed(2)}/year ({discount}% off)
              </div>
            )}
            {billingCycle === 'one-time' && price > 0 && (
              <div
                style={{
                  fontSize: '11px',
                  color: '#999',
                  fontWeight: '400',
                }}
              >
                ${(price / 36).toFixed(2)}/month for 3 years
              </div>
            )}
          </div>

          {/* Credits Display */}
          {credits > 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                background: 'rgba(139, 0, 0, 0.12)',
                borderRadius: '6px',
                border: '1px solid rgba(139, 0, 0, 0.2)',
              }}
            >
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: '#ff6b6b',
                  lineHeight: '1',
                }}
              >
                {formattedCredits}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#aaa',
                  fontWeight: '400',
                }}
              >
                credits/month
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Middle Section: Description (Scrollable) */}
      <div
        className="plan-card-description-scroll"
        style={{
          flex: '1 1 auto',
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          marginBottom: '20px',
        }}
      >
        {features.length > 0 ? (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
          >
            {features.map((feature, index) => (
              <li
                key={index}
                style={{
                  padding: '8px 0',
                  fontSize: '14px',
                  color: '#ccc',
                  display: 'flex',
                  alignItems: 'start',
                  gap: '8px',
                }}
              >
                <span style={{ 
                  color: feature.source === 'tier' ? '#ff6b6b' : '#0f0', 
                  fontSize: '16px',
                  fontWeight: feature.source === 'tier' ? 'bold' : 'normal'
                }}>
                  {feature.source === 'tier' ? '+' : '✓'}
                </span>
                <span>{feature.text.replace(/^-\s*/, '')}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{
            fontSize: '14px',
            color: '#999',
            fontStyle: 'italic',
          }}>
            No description available
          </p>
        )}
      </div>
    </div>
  );
}

export default PlanSwitchCard;
